import { MeetingProviderContex } from './meetingProviderContextDef';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { eventEmitter, events } from '../utils';
import { VideoSDK } from '@videosdk.live/js-sdk';
import { version as reactSDKVersion } from '../../package.json';

/**
 *
 * @param {{
 *  children:any,
 *  config:{
 *    meetingId: string,
 *    autoConsume?: boolean,
 *    preferredProtocol?:"UDP_ONLY" | "UDP_OVER_TCP" | "TCP_ONLY",
 *    participantId?:string | undefined,
 *    name:string,
 *    micEnabled:boolean,
 *    webcamEnabled:boolean,
 *    maxResolution?:"hd" | "sd",
 *    customCameraVideoTrack?: MediaStream | undefined
 *    customMicrophoneAudioTrack?: MediaStream | undefined,
 *    multiStream?: boolean,
 *    mode?: "SEND_AND_RECV" | "SIGNALLING_ONLY" | "RECV_ONLY",
 *    metaData?: object,
 *    defaultCamera?: 'front' | 'back',
 *    debugMode: boolean,
 * },
 *  token: string,
 *  joinWithoutUserInteraction?: boolean,
 *  reinitialiseMeetingOnConfigChange?: boolean,
 *  deviceInfo?: object
 * }}
 *
 */
const MeetingProvider = ({
  children,
  config,
  token,
  joinWithoutUserInteraction,
  reinitialiseMeetingOnConfigChange: _reinitialiseMeetingOnConfigChange,
  deviceInfo,
  keyProvider
}) => {
  const [meeting, setMeeting] = useState(null);
  const [localParticipant, setLocalParticipant] = useState(null);
  const [mainParticipant, setMainParticipant] = useState(null);
  const [activeSpeakerId, setActiveSpeakerId] = useState(null);
  const [presenterId, setPresenterId] = useState(null);
  const [localMicOn, setLocalMicOn] = useState(false);
  const [localWebcamOn, setLocalWebcamOn] = useState(false);
  const [localScreenShareOn, setLocalScreenShareOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState(new Map());
  const [characters, setCharacters] = useState(new Map());
  const [pinnedParticipants, setPinnedParticipants] = useState(new Map());
  const [isLiveStreaming, setIsLiveStreaming] = useState(false);
  const [connections, setConnections] = useState(new Map());
  const [isMeetingJoined, setIsMeetingJoined] = useState(false);
  const [isHls, setIsHls] = useState(false);
  const [recordingState, setRecordingState] = useState('RECORDING_STOPPED');
  const [livestreamState, setLivestreamState] = useState('LIVESTREAM_STOPPED');
  const [hlsState, setHlsState] = useState('HLS_STOPPED');
  const [hlsUrls, setHlsUrls] = useState({
    downstreamUrl: null,
    playbackHlsUrl: null,
    livestreamUrl: null
  });
  const [transcriptionState, setTranscriptionState] = useState(
    'TRANSCRIPTION_STOPPED'
  );

  const meetingRef = useRef();
  const localMicOnRef = useRef();
  const localWebcamOnRef = useRef();
  const localScreenShareOnRef = useRef();
  const isRecordingRef = useRef();
  const keyProviderRef = useRef(keyProvider);
  const configRef = useRef(config);
  const tokenRef = useRef(token);
  const joinedOnFirstRender = useRef(false);
  const isHlsRef = useRef();

  const reinitialiseMeetingOnConfigChange = useMemo(
    () => _reinitialiseMeetingOnConfigChange,
    []
  );

  useEffect(() => {
    meetingRef.current = meeting;
  }, [meeting]);
  useEffect(() => {
    localMicOnRef.current = localMicOn;
  }, [localMicOn]);
  useEffect(() => {
    localWebcamOnRef.current = localWebcamOn;
  }, [localWebcamOn]);
  useEffect(() => {
    localScreenShareOnRef.current = localScreenShareOn;
  }, [localScreenShareOn]);
  useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    isHlsRef.current = isHls;
  }, [isHls]);

  const resetStates = () => {
    setMeeting(null);
    setLocalParticipant(null);
    setMainParticipant(null);
    setActiveSpeakerId(null);
    setPresenterId(null);
    setLocalMicOn(false);
    setLocalWebcamOn(false);
    setLocalScreenShareOn(false);
    setIsRecording(false);
    setMessages([]);
    setIsLiveStreaming(false);
    setParticipants(new Map());
    setCharacters(new Map());
    setPinnedParticipants(new Map());
    setConnections(new Map());
    setIsMeetingJoined(false);
    setIsHls(false);
    setRecordingState('RECORDING_STOPPED');
    setLivestreamState('LIVESTREAM_STOPPED');
    setHlsState('HLS_STOPPED');
    setTranscriptionState('TRANSCRIPTION_STOPPED');
    setHlsUrls({
      downstreamUrl: null,
      playbackHlsUrl: null,
      livestreamUrl: null
    });
  };

  const _handle_participant_joined = participant => {
    setParticipants(participants => {
      participants.set(participant.id, participant);
      const participantsToSet = new Map(participants);
      return participantsToSet;
    });
    eventEmitter.emit(events['participant-joined'], participant);
  };
  const _handle_participant_left = participant => {
    setParticipants(participants => {
      participants.delete(participant.id);

      const newParticipants = new Map(participants);
      return newParticipants;
    });
    eventEmitter.emit(events['participant-left'], participant);
  };
  const _handle_presenter_changed = presenterId => {
    setPresenterId(presenterId);
    setLocalScreenShareOn(
      presenterId === meetingRef?.current?.localParticipant?.id
    );
    eventEmitter.emit(events['presenter-changed'], presenterId);
  };
  const _handle_main_participant_changed = participant => {
    setMainParticipant(participant);
    eventEmitter.emit(events['main-participant-changed'], participant);
  };
  const _handle_speaker_changed = activeSpeakerId => {
    setActiveSpeakerId(activeSpeakerId);
    eventEmitter.emit(events['speaker-changed'], activeSpeakerId);
  };
  const _handle_chat_message = data => {
    setMessages(s => [...s, data]);

    eventEmitter.emit(events['chat-message'], data);
  };
  const _handle_entry_requested = data => {
    eventEmitter.emit(events['entry-requested'], data);
  };
  const _handle_entry_responded = (participantId, decision) => {
    eventEmitter.emit(events['entry-responded'], participantId, decision);
  };
  const _handle_recording_started = () => {
    setIsRecording(true);
    eventEmitter.emit(events['recording-started']);
  };
  const _handle_recording_stopped = () => {
    setIsRecording(false);
    eventEmitter.emit(events['recording-stopped']);
  };

  const _handle_localParticipant_stream_enabled = stream => {
    if (stream.track.readyState === 'live') {
      if (stream.kind === 'video') {
        setLocalWebcamOn(true);
      } else if (stream.kind === 'audio') {
        setLocalMicOn(true);
      }
    }
  };
  const _handle_localParticipant_stream_disabled = stream => {
    if (stream.kind === 'video') {
      setLocalWebcamOn(false);
    } else if (stream.kind === 'audio') {
      setLocalMicOn(false);
    }
  };

  const _handle_live_stream_started = data => {
    setIsLiveStreaming(true);
    eventEmitter.emit(events['live-stream-started'], data);
  };
  const _handle_live_stream_stopped = () => {
    setIsLiveStreaming(false);
    eventEmitter.emit(events['live-stream-stopped']);
  };
  const _handle_hls_started = data => {
    setIsHls(true);
    eventEmitter.emit(events['hls-started'], data);
  };
  const _handle_hls_stopped = () => {
    setIsHls(false);
    eventEmitter.emit(events['hls-stopped']);
  };
  const _handle_video_state_changed = data => {
    eventEmitter.emit(events['video-state-changed'], data);
  };
  const _handle_video_seeked = data => {
    eventEmitter.emit(events['video-seeked'], data);
  };

  const _handle_webcam_requested = data => {
    eventEmitter.emit(events['webcam-requested'], data);
  };

  const _handle_mic_requested = data => {
    eventEmitter.emit(events['mic-requested'], data);
  };

  const _handle_meeting_joined = data => {
    if (data.switchRoomId) {
      setMessages(s => [...s, ...data.messages]);
      eventEmitter.emit(events['meeting-joined'], data);
    } else {
      joinedOnFirstRender.current = true;
      setMessages(s => [...s, ...data.messages]);
      eventEmitter.emit(events['meeting-joined'], data);
      setIsMeetingJoined(true);
    }
  };

  const _handle_meeting_left = () => {
    eventEmitter.emit(events['meeting-left']);

    const meeting = meetingRef.current;

    if (meeting) {
      if (typeof meeting?.off === 'function') {
        meeting.off('meeting-state-changed', _handle_meeting_state_changed);
        meeting.off('participant-joined', _handle_participant_joined);
        meeting.off('participant-left', _handle_participant_left);
        meeting.off('presenter-changed', _handle_presenter_changed);
        meeting.off(
          'main-participant-changed',
          _handle_main_participant_changed
        );
        meeting.off('speaker-changed', _handle_speaker_changed);
        meeting.off('entry-requested', _handle_entry_requested);
        meeting.off('entry-responded', _handle_entry_responded);
        meeting.off('chat-message', _handle_chat_message);
        meeting.off('recording-started', _handle_recording_started);
        meeting.off('recording-stopped', _handle_recording_stopped);
        meeting.off('meeting-joined', _handle_meeting_joined);
        meeting.off('meeting-left', _handle_meeting_left);
        meeting.off('livestream-started', _handle_live_stream_started);
        meeting.off('livestream-stopped', _handle_live_stream_stopped);
        meeting.off('video-state-changed', _handle_video_state_changed);
        meeting.off('video-seeked', _handle_video_seeked);
        meeting.off('webcam-requested', _handle_webcam_requested);
        meeting.off('mic-requested', _handle_mic_requested);
        meeting.off('pin-state-changed', _handle_pin_state_changed);
        meeting.off('paused-all-streams', _handle_streams_paused);
        meeting.off('resumed-all-streams', _handle_streams_resumed);
        meeting.off('connection-open', _handle_connection_open);
        meeting.off('connection-close', _handle_connection_close);
        meeting.off('switch-meeting', _handle_switch_meeting);
        meeting.off('error', _handle_error);
        meeting.off('hls-started', _handle_hls_started);
        meeting.off('hls-stopped', _handle_hls_stopped);
        meeting.off('recording-state-changed', _handle_recording_state_changed);
        meeting.off(
          'livestream-state-changed',
          _handle_livestream_state_changed
        );
        meeting.off('hls-state-changed', _handle_hls_state_changed);
        meeting.off(
          'participant-mode-changed',
          _handel_participant_mode_changed
        );
        meeting.off('whiteboard-stopped', _handle_whiteboard_stopped);
        meeting.off('whiteboard-started', _handle_whiteboard_started);
        meeting.off(
          'transcription-state-changed',
          _handle_transcription_state_changed
        );
        meeting.off('character-joined', _handle_character_joined);
        meeting.off('character-left', _handle_character_left);
      }

      if (typeof meeting?.localParticipant?.off === 'function') {
        meeting.localParticipant.off(
          'stream-enabled',
          _handle_localParticipant_stream_enabled
        );

        meeting.localParticipant.off(
          'stream-disabled',
          _handle_localParticipant_stream_disabled
        );
      }

      resetStates();
    }
  };

  const _handle_pin_state_changed = ({ participantId, state, pinnedBy }) => {
    setPinnedParticipants(pinnedParticipants => {
      if (!state.cam && !state.share) {
        pinnedParticipants.delete(participantId);
      } else {
        pinnedParticipants.set(participantId, state);
      }

      const pinnedParticipantsToSet = new Map(pinnedParticipants);

      return pinnedParticipantsToSet;
    });

    eventEmitter.emit(events['pin-state-changed'], {
      participantId,
      state,
      pinnedBy
    });
  };

  const _handle_streams_paused = ({ kind }) => {
    eventEmitter.emit(events['paused-all-streams'], {
      kind
    });
  };

  const _handle_streams_resumed = ({ kind }) => {
    eventEmitter.emit(events['resumed-all-streams'], {
      kind
    });
  };

  const _handle_connection_open = connection => {
    setConnections(s => {
      s.set(connection.id, connection);
      const connectionsToSet = new Map(s);
      return connectionsToSet;
    });
    eventEmitter.emit(events['connection-open'], connection);
  };

  const _handle_connection_close = connectionId => {
    setConnections(s => {
      s.delete(connectionId);
      const newConnections = new Map(s);
      return newConnections;
    });
    eventEmitter.emit(events['connection-close'], connectionId);
  };

  const _handle_switch_meeting = d => {
    eventEmitter.emit(events['switch-meeting'], d);
  };
  const _handle_error = data => {
    eventEmitter.emit(events['error'], data);
  };

  const _handle_recording_state_changed = data => {
    setRecordingState(data?.status);
    eventEmitter.emit(events['recording-state-changed'], data);
  };
  const _handle_livestream_state_changed = data => {
    setLivestreamState(data?.status);
    eventEmitter.emit(events['livestream-state-changed'], data);
  };
  const _handle_hls_state_changed = data => {
    setHlsState(data?.status);
    if (data?.status == 'HLS_PLAYABLE') {
      setHlsUrls({
        downstreamUrl: data?.downstreamUrl,
        playbackHlsUrl: data?.playbackHlsUrl,
        livestreamUrl: data?.livestreamUrl
      });
    } else if (data?.status == 'HLS_STOPPED') {
      setHlsUrls({
        downstreamUrl: null,
        playbackHlsUrl: null,
        livestreamUrl: null
      });
    }
    eventEmitter.emit(events['hls-state-changed'], data);
  };

  const _handle_meeting_state_changed = data => {
    eventEmitter.emit(events['meeting-state-changed'], data);
  };

  const _handle_transcription_state_changed = data => {
    setTranscriptionState(data?.status);
    eventEmitter.emit(events['transcription-state-changed'], data);
  };

  const _handle_transcription_text = data => {
    eventEmitter.emit(events['transcription-text'], data);
  };

  const _handle_whiteboard_started = data => {
    eventEmitter.emit(events['whiteboard-started'], data);
  };

  const _handle_whiteboard_stopped = data => {
    eventEmitter.emit(events['whiteboard-stopped'], data);
  };

  const _handle_character_joined = character => {
    setCharacters(characters => {
      characters.set(character.id, character);
      const charactersToSet = new Map(characters);
      return charactersToSet;
    });
    eventEmitter.emit(events['character-joined'], character);
  };

  const _handle_character_left = character => {
    setCharacters(characters => {
      characters.delete(character.id);

      const newCharacters = new Map(characters);
      return newCharacters;
    });
    eventEmitter.emit(events['character-left'], character);
  };

  const _handel_participant_mode_changed = data => {
    setParticipants(participants => {
      const part = participants.get(data.participantId);
      part.mode = data.mode;
      participants.set(part.id, part);
      const participantsToSet = new Map(participants);
      return participantsToSet;
    });

    // Update Local Participant Object
    if (data.participantId === meetingRef?.current?.localParticipant?.id) {
      setLocalParticipant(participant => {
        participant.mode = data.mode;
        return participant;
      });
    }

    eventEmitter.emit(events['participant-mode-changed'], data);
  };

  const _handle_media_relay_started = data => {
    eventEmitter.emit(events['media-relay-started'], data);
  };
  const _handle_media_relay_stopped = data => {
    eventEmitter.emit(events['media-relay-stopped'], data);
  };
  const _handle_media_relay_error = data => {
    eventEmitter.emit(events['media-relay-error'], data);
  };
  const _handle_media_relay_request_response = data => {
    eventEmitter.emit(events['media-relay-request-response'], data);
  };
  const _handle_media_relay_request_received = data => {
    eventEmitter.emit(events['media-relay-request-received'], data);
  };

  const join = () => {
    const meeting = VideoSDK && VideoSDK.initMeeting(configRef.current);
    setMeeting(meeting);
    const { localParticipant, participants, characters } = meeting;
    participants.set(localParticipant.id, localParticipant);
    setParticipants(participants);
    setCharacters(characters);
    setLocalParticipant(localParticipant);
    meeting.on('meeting-state-changed', _handle_meeting_state_changed);
    meeting.on('participant-joined', _handle_participant_joined);
    meeting.on('participant-left', _handle_participant_left);
    meeting.on('presenter-changed', _handle_presenter_changed);
    meeting.on('main-participant-changed', _handle_main_participant_changed);
    meeting.on('speaker-changed', _handle_speaker_changed);
    meeting.on('entry-requested', _handle_entry_requested);
    meeting.on('entry-responded', _handle_entry_responded);
    meeting.on('chat-message', _handle_chat_message);
    meeting.on('recording-started', _handle_recording_started);
    meeting.on('recording-stopped', _handle_recording_stopped);
    meeting.on('meeting-joined', _handle_meeting_joined);
    meeting.on('meeting-left', _handle_meeting_left);
    meeting.on('livestream-started', _handle_live_stream_started);
    meeting.on('livestream-stopped', _handle_live_stream_stopped);
    meeting.on('video-state-changed', _handle_video_state_changed);
    meeting.on('video-seeked', _handle_video_seeked);
    meeting.on('webcam-requested', _handle_webcam_requested);
    meeting.on('mic-requested', _handle_mic_requested);
    meeting.on('pin-state-changed', _handle_pin_state_changed);
    meeting.on('paused-all-streams', _handle_streams_paused);
    meeting.on('resumed-all-streams', _handle_streams_resumed);
    meeting.on('connection-open', _handle_connection_open);
    meeting.on('connection-close', _handle_connection_close);
    meeting.on('switch-meeting', _handle_switch_meeting);
    meeting.on('error', _handle_error);
    meeting.on('hls-started', _handle_hls_started);
    meeting.on('hls-stopped', _handle_hls_stopped);
    meeting.on('recording-state-changed', _handle_recording_state_changed);
    meeting.on('livestream-state-changed', _handle_livestream_state_changed);
    meeting.on('hls-state-changed', _handle_hls_state_changed);
    meeting.on('participant-mode-changed', _handel_participant_mode_changed);
    meeting.on(
      'transcription-state-changed',
      _handle_transcription_state_changed
    );
    meeting.on('transcription-text', _handle_transcription_text);
    meeting.on('whiteboard-started', _handle_whiteboard_started);
    meeting.on('whiteboard-stopped', _handle_whiteboard_stopped);
    meeting.on('character-joined', _handle_character_joined);
    meeting.on('character-left', _handle_character_left);

    meeting.on('media-relay-started', _handle_media_relay_started);
    meeting.on('media-relay-stopped', _handle_media_relay_stopped);
    meeting.on('media-relay-error', _handle_media_relay_error);
    meeting.on(
      'media-relay-request-response',
      _handle_media_relay_request_response
    );
    meeting.on(
      'media-relay-request-received',
      _handle_media_relay_request_received
    );

    meeting.localParticipant.on(
      'stream-enabled',
      _handle_localParticipant_stream_enabled
    );
    meeting.localParticipant.on(
      'stream-disabled',
      _handle_localParticipant_stream_disabled
    );
    meeting.join();
  };

  const leave = () => {
    const meeting = meetingRef.current;

    if (meeting) {
      meeting.leave();
    }
  };

  const end = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.end();
    }
  };

  const pauseAllStreams = kind => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.pauseAllStreams(kind);
    }
  };

  const resumeAllStreams = kind => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.resumeAllStreams(kind);
    }
  };

  const startRecording = (webhookUrl, awsDirPath, config, transcription) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.startRecording(webhookUrl, awsDirPath, config, transcription);
    }
  };
  const stopRecording = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      const isRecording = isRecordingRef.current;
      if (isRecording) {
        meeting.stopRecording();
      }
    }
  };
  const unmuteMic = (customAudioTrack = undefined) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.unmuteMic(customAudioTrack);
    }
  };
  const muteMic = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.muteMic();
    }
  };
  const toggleMic = (customAudioTrack = undefined) => {
    const localMicOn = localMicOnRef.current;

    if (localMicOn) {
      muteMic();
    } else {
      unmuteMic(customAudioTrack);
    }
  };
  const disableWebcam = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.disableWebcam();
    }
  };
  const enableWebcam = (customVideoTrack = undefined) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.enableWebcam(customVideoTrack);
    }
  };
  const toggleWebcam = (customVideoTrack = undefined) => {
    const localWebcamOn = localWebcamOnRef.current;

    if (localWebcamOn) {
      disableWebcam();
    } else {
      enableWebcam(customVideoTrack);
    }
  };

  const switchTo = ({ meetingId, token }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.switchTo({ meetingId, token });
    }
  };

  const disableScreenShare = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.disableScreenShare();
    }
  };
  const enableScreenShare = (customScreenShareTrack = undefined) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.enableScreenShare(customScreenShareTrack);
    }
  };

  const toggleScreenShare = (customScreenShareTrack = undefined) => {
    const localScreenShareOn = localScreenShareOnRef.current;

    if (localScreenShareOn) {
      disableScreenShare();
    } else {
      enableScreenShare(customScreenShareTrack);
    }
  };

  const changeMode = mode => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.changeMode(mode);
    }
  };

  const sendChatMessage = text => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.sendChatMessage(text);
    }
  };
  const respondEntry = (participantId, decision) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.respondEntry(participantId, decision);
    }
  };
  const getMics = async () => {
    const meeting = meetingRef.current;
    if (meeting) {
      return await meeting.getMics();
    }
  };
  const getWebcams = async () => {
    const meeting = meetingRef.current;
    if (meeting) {
      return await meeting.getWebcams();
    }
  };
  const changeWebcam = obj => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.changeWebcam(obj);
    }
  };
  const replaceWebcamStream = stream => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.replaceWebcamStream(stream);
    }
  };
  const changeMic = obj => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.changeMic(obj);
    }
  };
  const startVideo = ({ link }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.startVideo({ link });
    }
  };

  const stopVideo = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.stopVideo();
    }
  };

  const resumeVideo = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.resumeVideo();
    }
  };

  const pauseVideo = ({ currentTime }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.pauseVideo({ currentTime });
    }
  };

  const enableAdaptiveSubscription = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.enableAdaptiveSubscription();
    }
  };

  const disableAdaptiveSubscription = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.disableAdaptiveSubscription();
    }
  };

  const requestMediaRelay = ({ destinationMeetingId, token, kinds }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.requestMediaRelay({ destinationMeetingId, token, kinds });
    }
  };

  const stopMediaRelay = destinationMeetingId => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.stopMediaRelay(destinationMeetingId);
    }
  };

  const seekVideo = ({ currentTime }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.seekVideo({ currentTime });
    }
  };

  const startLivestream = (streamInfo, config) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.startLivestream(streamInfo, config);
    }
  };

  const stopLivestream = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.stopLivestream();
    }
  };

  const connectTo = async ({ meetingId, payload }) => {
    const meeting = meetingRef.current;
    if (meeting) {
      await meeting.connectTo({ meetingId, payload });
    }
  };

  const startHls = (config, transcription) => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.startHls(config, transcription);
    }
  };

  const stopHls = () => {
    const meeting = meetingRef.current;
    if (meeting) {
      meeting.stopHls();
    }
  };

  const initSDK = () => {
    VideoSDK.config(tokenRef.current);
    keyProviderRef.current && VideoSDK.setKeyProvider(keyProviderRef.current);
    if (deviceInfo) {
      VideoSDK.analytics(deviceInfo);
    } else {
      VideoSDK.analytics({
        sdkType: 'react-web',
        sdkVersion: reactSDKVersion
      });
    }

    if (joinWithoutUserInteraction) {
      join();
    }
  };

  useEffect(() => {
    initSDK();
  }, []);

  useEffect(() => {
    if (reinitialiseMeetingOnConfigChange && joinedOnFirstRender.current) {
      leave();
      initSDK();
      !joinWithoutUserInteraction && join();
    }
  }, [config.meetingId, token, reinitialiseMeetingOnConfigChange]);

  return (
    <MeetingProviderContex.Provider
      value={{
        meetingId: meeting?.id,
        meeting,
        baseUrl: meeting?.baseUrl,
        localParticipant,
        mainParticipant,
        activeSpeakerId,
        participants,
        characters,
        presenterId,
        localMicOn,
        localWebcamOn,
        localScreenShareOn,
        messages,
        isRecording,
        isLiveStreaming,
        pinnedParticipants,
        connections,
        isHls,
        recordingState,
        livestreamState,
        hlsState,
        hlsUrls,
        transcriptionState,
        isMeetingJoined,
        selectedCameraDevice: meeting?.selectedCameraDevice,
        selectedMicrophoneDevice: meeting?.selectedMicrophoneDevice,
        //
        join,
        leave,
        end,
        //
        pauseAllStreams,
        resumeAllStreams,
        //
        startRecording,
        stopRecording,
        //
        sendChatMessage,
        respondEntry,
        //
        muteMic,
        unmuteMic,
        toggleMic,
        //
        disableWebcam,
        enableWebcam,
        toggleWebcam,
        //
        disableScreenShare,
        enableScreenShare,
        toggleScreenShare,
        //

        switchTo,
        getMics,
        getWebcams,
        changeWebcam,
        replaceWebcamStream,
        changeMic,
        changeMode,
        startVideo,
        stopVideo,
        resumeVideo,
        pauseVideo,
        seekVideo,
        startLivestream,
        stopLivestream,
        connectTo,

        startHls,
        stopHls,

        enableAdaptiveSubscription,
        disableAdaptiveSubscription,

        requestMediaRelay,
        stopMediaRelay
      }}
    >
      {children}
    </MeetingProviderContex.Provider>
  );
};

export default MeetingProvider;
